import axios from "axios";
import { Country } from "../features/countries/types";
import { StatisticsData } from "../features/statistics/types";
import { redirect } from "react-router-dom";
import {
  loadSessionToken,
  removeSessionToken,
  saveSessionToken,
} from "../store/localStorage";
import { PinsResponse } from "../pages/mainPage/Statistics/Map/Pins/types";
import { baseURL, regionEnv, REGIONS_NAME } from "../config";
import { User } from "../features/users/types";
import { Region } from "../features/regions/types";

if (!baseURL) {
  throw new Error("REACT_APP_API_BASE_URL not found");
}

export const axiosInstance = axios.create({
  baseURL,
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      removeSessionToken();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.request.use((config) => {
  const sessionToken = loadSessionToken();
  if (!sessionToken) {
    window.location.href = "/login";
    return Promise.reject(new Error("No session token"));
  }
  config.headers["Authorization"] = sessionToken;
  return config;
});

const downloadFile = async (url: string, defaultFilename: string) => {
  const { headers, data } = await axiosInstance.get(url, {
    responseType: "blob",
  });
  const filename: string | undefined = headers["file-name"];
  const downloadableUrl: string = window.URL.createObjectURL(data);
  const anchorElement: HTMLAnchorElement = document.createElement("a");
  anchorElement.href = downloadableUrl;
  anchorElement.download = filename || defaultFilename;
  anchorElement.click();
  window.URL.revokeObjectURL(downloadableUrl);
};

export const api = {
  getMap: async (year: string): Promise<JSON> =>
    (await axiosInstance.get(`/map/${year}`)).data || [],

  getPins: async (year: string): Promise<PinsResponse[]> =>
    (await axiosInstance.get(`/pins/${year}`)).data || [],

  getStatistics: async (year: string): Promise<StatisticsData[]> =>
    (await axiosInstance.get(`/statistics/${year}`)).data || {},

  getCountriesPerUser: async (): Promise<Country[]> =>
    (await axiosInstance.get("/countries")).data || [],

  getAllCountries: async (): Promise<Country[]> =>
    (await axiosInstance.get("/admin/allCountries")).data || [],

  getRegions: async (): Promise<Region[]> =>
    (await axiosInstance.get("/admin/allRegions")).data || [],

  getUsers: async (): Promise<User[]> =>
    (await axiosInstance.get("/admin/allUsers")).data || [],

  addOrUpdateUser: async (user: User): Promise<User | null> => {
    try {
      const response = await axiosInstance.post(`/admin/upsertUser`, user);
      return response.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  deleteUser: async (userId: string): Promise<void> =>
    await axiosInstance.delete(`/admin/upsertUser/${userId}`),

  getStateMap: async (stateId: string, year: string): Promise<JSON> =>
    (await axiosInstance.get(`/state/${stateId}/map/${year}`)).data || [],

  getStateStatistics: async (stateId: string, year: string): Promise<StatisticsData[]> =>
    (await axiosInstance.get(`/state/${stateId}/statistics/${year}`)).data || [],

  getDistrictStatistics: async (stateId: string, districtNum: string, year: string): Promise<StatisticsData[]> =>
    (await axiosInstance.get(`/state/${stateId}/district/${districtNum}/statistics/${year}`)).data || [],

  getInfographic: (year: string, iso3: string, stateId?: string, districtNum?: string): Promise<void> => {
    let url = `/infographic/${year}/${iso3}`;
    if (stateId) url += `/state/${stateId}`;
    if (stateId && districtNum) url += `/district/${districtNum}`;
    return downloadFile(url, "Infographic.pdf").catch((e) =>
      console.error(e)
    );
  },
  getMethodology: (): Promise<void> =>
    downloadFile("/methodology", "Methodology.pdf").catch((e) =>
      console.error(e)
    ),
  getReport: (year: string, iso3: string): Promise<void> =>
    downloadFile(`/report/${year}/${iso3}`, "Report.pdf").catch((e) =>
      console.error(e)
    ),
};

export const loginUser = async (
  email: string,
  password: string
): Promise<boolean> => {
  const token = await requestLogin(email, password, regionEnv as REGIONS_NAME);
  if (!token) {
    return false;
  }
  saveSessionToken(token);
  return true;
};

const requestLogin = async (
  email: string,
  password: string,
  region: REGIONS_NAME
): Promise<string> => {
  try {
    const response = await axios.post(`${baseURL}/login`, {
      email,
      password,
      region,
    });
    return response?.data?.token || "";
  } catch (e) {
    console.error(e);
    return "";
  }
};
